import { DynamicEventsResponse, FunnelEvents } from "@/api/insights";
import { ConversionEventsData } from "./types";

export function savedIds(saved: DynamicEventsResponse) {
  return {
    bottomFunnelIds: (saved.bottomFunnel ?? []).map((e) => e.eventDefId),
    midFunnelIds: (saved.midFunnel ?? []).map((e) => e.eventDefId),
  };
}

function sameIds(a: string[], b: string[]): boolean {
  const set = new Set(a);
  return set.size === new Set(b).size && b.every((id) => set.has(id));
}

/** Whether the saved conversions changed after this session was seeded. */
export function isSelectionStale(
  data: ConversionEventsData,
  saved: DynamicEventsResponse,
): boolean {
  const seededBottom = data.seededBottomFunnelIds ?? null;
  const seededMid = data.seededMidFunnelIds ?? null;
  if (seededBottom === null || seededMid === null) {
    return false;
  }
  const current = savedIds(saved);
  return (
    !sameIds(seededBottom, current.bottomFunnelIds) ||
    !sameIds(seededMid, current.midFunnelIds)
  );
}

/** Saved conversions that the wizard's selection no longer includes. */
export function removedConversions(
  data: ConversionEventsData,
  saved: DynamicEventsResponse,
): FunnelEvents[] {
  const kept = new Set([
    ...(data.bottomFunnelIds ?? []),
    ...(data.midFunnelIds ?? []),
  ]);
  return [...(saved.bottomFunnel ?? []), ...(saved.midFunnel ?? [])].filter(
    (event) => !kept.has(event.eventDefId),
  );
}

/**
 * Lookup for every event the selection can reference: the saved conversions,
 * which may be hidden or missing from the picker, overlaid with the picker's
 * options.
 */
export function eventsLookup(
  options: FunnelEvents[],
  saved: DynamicEventsResponse | undefined,
): Map<string, FunnelEvents> {
  return new Map(
    [
      ...(saved?.bottomFunnel ?? []),
      ...(saved?.midFunnel ?? []),
      ...options,
    ].map((event) => [event.eventDefId, event]),
  );
}

function placeholderEvent(eventDefId: string): FunnelEvents {
  return {
    eventDefId,
    name: "",
    funnelStage: "",
    conversionSource: "",
    isRecommended: false,
    isOther: false,
    isHidden: false,
    description: "",
    totalEventsDelivered: 0,
    isHighVolumeEvent: false,
    adPlatformsDelivered: 0,
    adPlatformsDeliveredList: "",
    isSpecialCaseAdsDelivPv: false,
    hasNoRecentTraffic: false,
    tags: {},
    type: "",
  };
}

/**
 * Builds the set_dynamic_events body from the selected IDs, so a selected
 * event keeps its place even when the lookup has no details for it. The
 * backend clears the revenue default when the key is omitted, so the saved
 * value is echoed back.
 */
export function buildSavePayload(
  data: ConversionEventsData,
  lookup: Map<string, FunnelEvents>,
  saved: DynamicEventsResponse,
): DynamicEventsResponse {
  const toEvents = (ids: string[] | null) =>
    (ids ?? []).map((id) => lookup.get(id) ?? placeholderEvent(id));
  return {
    bottomFunnel: toEvents(data.bottomFunnelIds),
    midFunnel: toEvents(data.midFunnelIds),
    lowFunnelDefaultValueMicros: saved.lowFunnelDefaultValueMicros ?? null,
  };
}
